import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import UpperCaseInput from '../components/UpperCaseInput';
import RutInput from '../components/RutInput';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Building, Loader2 } from 'lucide-react';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImportadoraImage = ({ importadora, token }) => {
  const [imgSrc, setImgSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!importadora.imagen_url) { setError(true); return; }
    setLoading(true);
    setError(false);
    axios.get(`${API}/importadoras/${importadora.id}/logo`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => { setImgSrc(res.data.base64); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [importadora.id, importadora.imagen_url, token]);

  if (error || (!loading && !imgSrc)) {
    return (
      <div className="h-40 bg-slate-100 flex items-center justify-center">
        <Building size={48} className="text-slate-400" />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="h-40 bg-slate-100 flex items-center justify-center">
        <Loader2 size={24} className="text-slate-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-40 bg-slate-100 flex items-center justify-center overflow-hidden">
      <img
        src={imgSrc}
        alt={importadora.razon_social}
        className="h-full w-full object-contain p-4"
      />
    </div>
  );
};

const Importadoras = () => {
  const { token } = useAuth();
  const [importadoras, setImportadoras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedImportadora, setSelectedImportadora] = useState(null);
  const [formData, setFormData] = useState({ razon_social: '', rut: '', alias: '', giro: '', direccion: '', comuna: '' });
  const [imagen, setImagen] = useState(null);
  const [imagenPreview, setImagenPreview] = useState(null);
  const [justification, setJustification] = useState('');
  const [rutValid, setRutValid] = useState(false);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchImportadoras();
  }, []);

  const fetchImportadoras = async () => {
    try {
      const response = await axios.get(`${API}/importadoras`);
      setImportadoras(response.data);
    } catch (error) {
      toast.error('Error al cargar importadores');
    } finally {
      setLoading(false);
    }
  };

  const handleRutChange = (e) => {
    setFormData({ ...formData, rut: e.target.value });
  };

  const handleRutValidChange = (isValid) => {
    setRutValid(isValid);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImagen(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagenPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const toUpperCase = (text) => text ? text.toUpperCase() : '';

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!rutValid) {
      toast.error('El RUT ingresado no es válido');
      return;
    }
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('razon_social', toUpperCase(formData.razon_social));
      formDataToSend.append('rut', toUpperCase(formData.rut));
      formDataToSend.append('alias', toUpperCase(formData.alias));
      formDataToSend.append('giro', toUpperCase(formData.giro));
      formDataToSend.append('direccion', toUpperCase(formData.direccion));
      formDataToSend.append('comuna', toUpperCase(formData.comuna));
      if (imagen) {
        formDataToSend.append('imagen', imagen);
      }

      await axios.post(`${API}/importadoras`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Importador creado exitosamente');
      setIsCreateModalOpen(false);
      resetForm();
      fetchImportadoras();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear importador');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!justification.trim()) {
      toast.error('La justificación es obligatoria');
      return;
    }
    
    try {
      const formDataToSend = new FormData();
      if (formData.razon_social) formDataToSend.append('razon_social', toUpperCase(formData.razon_social));
      if (formData.rut) formDataToSend.append('rut', toUpperCase(formData.rut));
      if (formData.alias) formDataToSend.append('alias', toUpperCase(formData.alias));
      if (formData.giro) formDataToSend.append('giro', toUpperCase(formData.giro));
      if (formData.direccion) formDataToSend.append('direccion', toUpperCase(formData.direccion));
      if (formData.comuna) formDataToSend.append('comuna', toUpperCase(formData.comuna));
      formDataToSend.append('justification', toUpperCase(justification));
      if (imagen) {
        formDataToSend.append('imagen', imagen);
      }

      await axios.put(`${API}/importadoras/${selectedImportadora.id}`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Importador actualizado exitosamente');
      setIsEditModalOpen(false);
      resetForm();
      fetchImportadoras();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar importador');
    }
  };

  const handleDelete = async (importadoraId) => {
    if (!window.confirm('¿Estás seguro de eliminar este importador? Esta acción no se puede deshacer.')) {
      return;
    }
    
    setDeleting(importadoraId);
    
    try {
      console.log('Eliminando importadora:', importadoraId);
      const response = await axios.delete(`${API}/importadoras/${importadoraId}`);
      console.log('Respuesta del servidor:', response.data);
      
      // Actualizar lista inmediatamente
      setImportadoras(prev => prev.filter(imp => imp.id !== importadoraId));
      toast.success('Importador eliminado correctamente');
    } catch (error) {
      console.error('Error al eliminar:', error);
      const errorMessage = error.response?.data?.detail || 'Error al eliminar importador';
      toast.error(errorMessage);
    } finally {
      setDeleting(null);
    }
  };

  const resetForm = () => {
    setFormData({ razon_social: '', rut: '', alias: '', giro: '', direccion: '', comuna: '' });
    setImagen(null);
    setImagenPreview(null);
    setJustification('');
    setRutValid(false);
    setSelectedImportadora(null);
  };

  const openEditModal = (importadora) => {
    setSelectedImportadora(importadora);
    setFormData({
      razon_social: importadora.razon_social,
      rut: importadora.rut,
      alias: importadora.alias || '',
      giro: importadora.giro || '',
      direccion: importadora.direccion || '',
      comuna: importadora.comuna || ''
    });
    setRutValid(true); // El RUT existente ya es válido
    setImagenPreview(importadora.imagen_url ? `${API}${importadora.imagen_url}` : null);
    setIsEditModalOpen(true);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="importadoras-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-bold text-slate-900 tracking-tight">Empresas Importadoras</h1>
            <p className="text-slate-600 mt-1">Gestión de empresas importadoras de cereales</p>
          </div>
          <Button onClick={() => setIsCreateModalOpen(true)} data-testid="create-importadora-button" className="bg-primary hover:bg-primary-hover text-white rounded-sm">
            <Plus className="mr-2" size={18} />Crear Importador
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : importadoras.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 bg-white border border-slate-200 rounded-sm">
            <Building size={48} className="text-slate-400 mb-4" />
            <p className="text-slate-600">No hay empresas importadoras registradas</p>
            <Button onClick={() => setIsCreateModalOpen(true)} className="mt-4" variant="outline">
              <Plus className="mr-2" size={16} />Crear primera empresa
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {importadoras.map((importadora) => (
              <div key={importadora.id} data-testid={`importadora-card-${importadora.id}`} className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                <ImportadoraImage importadora={importadora} token={token} />
                <div className="p-4">
                  <h3 className="text-lg font-heading font-semibold text-slate-900 mb-2">{importadora.razon_social}</h3>
                  {importadora.alias && (
                    <p className="text-sm text-slate-500 italic mb-2">"{importadora.alias}"</p>
                  )}
                  <div className="space-y-1 text-sm text-slate-600">
                    <p><span className="font-medium">RUT:</span> <span className="font-mono">{importadora.rut}</span></p>
                    <p><span className="font-medium">Giro:</span> {importadora.giro}</p>
                    <p><span className="font-medium">Dirección:</span> {importadora.direccion}</p>
                    {importadora.comuna && (
                      <p><span className="font-medium">Comuna:</span> {importadora.comuna}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-200">
                    <Button 
                      onClick={() => openEditModal(importadora)} 
                      data-testid={`edit-importadora-${importadora.id}`} 
                      variant="ghost" 
                      size="sm" 
                      className="flex-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    >
                      <Pencil size={16} className="mr-1" /> Editar
                    </Button>
                    <Button 
                      onClick={() => handleDelete(importadora.id)} 
                      data-testid={`delete-importadora-${importadora.id}`} 
                      variant="ghost" 
                      size="sm" 
                      className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                      disabled={deleting === importadora.id}
                    >
                      {deleting === importadora.id ? (
                        <><Loader2 size={16} className="mr-1 animate-spin" /> Eliminando...</>
                      ) : (
                        <><Trash2 size={16} className="mr-1" /> Eliminar</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal Crear */}
        <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
          <DialogContent data-testid="create-importadora-modal" className="max-w-2xl">
            <DialogHeader><DialogTitle>Crear Importador</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label>Razón Social *</Label>
                  <UpperCaseInput 
                    value={formData.razon_social} 
                    onChange={(e) => setFormData({ ...formData, razon_social: e.target.value })} 
                    required 
                    data-testid="importadora-razon-input" 
                    placeholder="Nombre de la empresa" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>RUT *</Label>
                  <RutInput 
                    value={formData.rut} 
                    onChange={handleRutChange}
                    onValidChange={handleRutValidChange}
                    required 
                    data-testid="importadora-rut-input" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Alias *</Label>
                  <UpperCaseInput 
                    value={formData.alias} 
                    onChange={(e) => setFormData({ ...formData, alias: e.target.value })} 
                    required 
                    data-testid="importadora-alias-input" 
                    placeholder="Nombre corto" 
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Giro *</Label>
                  <UpperCaseInput 
                    value={formData.giro} 
                    onChange={(e) => setFormData({ ...formData, giro: e.target.value })} 
                    required 
                    data-testid="importadora-giro-input" 
                    placeholder="Ej: Importación de cereales" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Dirección *</Label>
                  <UpperCaseInput 
                    value={formData.direccion} 
                    onChange={(e) => setFormData({ ...formData, direccion: e.target.value })} 
                    required 
                    data-testid="importadora-direccion-input" 
                    placeholder="Dirección completa" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Comuna *</Label>
                  <UpperCaseInput 
                    value={formData.comuna} 
                    onChange={(e) => setFormData({ ...formData, comuna: e.target.value })} 
                    required 
                    data-testid="importadora-comuna-input" 
                    placeholder="Comuna" 
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Imagen / Logo</Label>
                  <Input type="file" accept="image/*" onChange={handleImageChange} data-testid="importadora-imagen-input" />
                  {imagenPreview && (
                    <div className="mt-2 border border-slate-200 rounded-sm p-2 bg-slate-50">
                      <img src={imagenPreview} alt="Preview" className="h-32 w-auto mx-auto object-contain" />
                    </div>
                  )}
                </div>
              </div>
              <Button type="submit" data-testid="submit-create-importadora" className="w-full bg-primary hover:bg-primary-hover text-white" disabled={!rutValid}>
                Crear Importador
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal Editar */}
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogContent data-testid="edit-importadora-modal" className="max-w-2xl">
            <DialogHeader><DialogTitle>Editar Importador</DialogTitle></DialogHeader>
            <form onSubmit={handleEdit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label>Razón Social</Label>
                  <UpperCaseInput 
                    value={formData.razon_social} 
                    onChange={(e) => setFormData({ ...formData, razon_social: e.target.value })} 
                    data-testid="edit-importadora-razon-input" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>RUT</Label>
                  <RutInput 
                    value={formData.rut} 
                    onChange={handleRutChange}
                    onValidChange={handleRutValidChange}
                    data-testid="edit-importadora-rut-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Alias</Label>
                  <UpperCaseInput 
                    value={formData.alias} 
                    onChange={(e) => setFormData({ ...formData, alias: e.target.value })} 
                    data-testid="edit-importadora-alias-input" 
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Giro</Label>
                  <UpperCaseInput 
                    value={formData.giro} 
                    onChange={(e) => setFormData({ ...formData, giro: e.target.value })} 
                    data-testid="edit-importadora-giro-input" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Dirección</Label>
                  <UpperCaseInput 
                    value={formData.direccion} 
                    onChange={(e) => setFormData({ ...formData, direccion: e.target.value })} 
                    data-testid="edit-importadora-direccion-input" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Comuna</Label>
                  <UpperCaseInput 
                    value={formData.comuna} 
                    onChange={(e) => setFormData({ ...formData, comuna: e.target.value })} 
                    data-testid="edit-importadora-comuna-input" 
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Imagen / Logo</Label>
                  <Input type="file" accept="image/*" onChange={handleImageChange} data-testid="edit-importadora-imagen-input" />
                  {imagenPreview && (
                    <div className="mt-2 border border-slate-200 rounded-sm p-2 bg-slate-50">
                      <img src={imagenPreview} alt="Preview" className="h-32 w-auto mx-auto object-contain" />
                    </div>
                  )}
                </div>
                <div className="space-y-2 col-span-2">
                  <Label className="text-red-600">Justificación (obligatoria) *</Label>
                  <UpperCaseInput 
                    value={justification} 
                    onChange={(e) => setJustification(e.target.value)} 
                    required 
                    placeholder="Motivo de la edición" 
                    data-testid="edit-importadora-justification-input" 
                  />
                </div>
              </div>
              <Button type="submit" data-testid="submit-edit-importadora" className="w-full bg-primary hover:bg-primary-hover text-white">
                Actualizar Importador
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Importadoras;
